import type { ApiClient } from "../api";
import { friendlyMessage } from "../api";
import type { Visit } from "../types";

export interface VisitedCtx {
  api: ApiClient;
  panelRoot: HTMLElement;
  notify: (msg: string) => void;
  onChanged: () => void;
}

function fmtDate(s: string): string {
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

function symbolFor(vt: 1 | 2): string {
  return vt === 2 ? "💩" : "💧";
}

export async function openVisitedPanel(ctx: VisitedCtx): Promise<void> {
  ctx.panelRoot.innerHTML = "";

  const sheet = document.createElement("section");
  sheet.className = "panel sheet";
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-label", "Visited list");

  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "panel-close";
  closeBtn.setAttribute("aria-label", "Close");
  closeBtn.textContent = "✕";
  closeBtn.addEventListener("click", () => {
    ctx.panelRoot.innerHTML = "";
  });
  sheet.appendChild(closeBtn);

  const h = document.createElement("h2");
  h.textContent = "Your visits";
  sheet.appendChild(h);

  const list = document.createElement("ul");
  list.className = "visit-list";
  sheet.appendChild(list);

  ctx.panelRoot.appendChild(sheet);

  let visits: Visit[];
  try {
    visits = await ctx.api.getVisits();
  } catch (err) {
    ctx.notify(friendlyMessage(err));
    return;
  }

  if (visits.length === 0) {
    const empty = document.createElement("p");
    empty.className = "muted";
    empty.textContent = "No visits yet. Tap a toilet to mark one.";
    sheet.appendChild(empty);
    return;
  }

  for (const v of visits) {
    const li = document.createElement("li");

    const sym = document.createElement("span");
    sym.className = "visit-sym";
    sym.textContent = symbolFor(v.visit_type);
    li.appendChild(sym);

    const meta = document.createElement("span");
    meta.className = "visit-meta";
    const md = (v.hydrated_metadata as { notes?: string; deleted?: boolean } | null) ?? null;
    const sourceLabel =
      v.source === "user" ? "user pin" : v.source === "osm-live" ? "OSM (live)" : "OSM (snapshot)";
    const dateStr = fmtDate(v.visited_at);
    meta.textContent = `${sourceLabel} - ${dateStr}`;
    li.appendChild(meta);

    if (md && typeof md.notes === "string") {
      const note = document.createElement("div");
      note.className = "visit-note";
      note.textContent = md.notes;
      li.appendChild(note);
    }
    if (md && md.deleted) {
      const stale = document.createElement("div");
      stale.className = "muted";
      stale.textContent = "this place may no longer exist";
      li.appendChild(stale);
    }

    const del = document.createElement("button");
    del.type = "button";
    del.className = "btn-secondary";
    del.textContent = "Remove";
    del.addEventListener("click", async () => {
      del.disabled = true;
      try {
        await ctx.api.deleteVisit(v.id);
        li.remove();
        ctx.onChanged();
      } catch (err) {
        ctx.notify(friendlyMessage(err));
        del.disabled = false;
      }
    });
    li.appendChild(del);

    list.appendChild(li);
  }
}
